import { AppLayoutPortal } from '@jotta/ui/AppLayoutPortal'
import * as Dialog from '@radix-ui/react-dialog'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useMediaObjectStore,
  useSelectionStore,
} from '../../store/PhotoContext'
import type { PhotoActionHandlersProps } from '../PhotoActions/PhotoActionHandlers'
import {
  PhotoActionHandler,
  PhotoActionHandlers,
} from '../PhotoActions/PhotoActionHandlers'
import { BaseSelectionManager } from './BaseSelectionManager'
import { getPhotoActions } from '../PhotoActions/PhotoActions.config'
import { useMobileBreakpoint } from '@jotta/ui/useBreakpoint'
import { Plural, t } from '@lingui/macro'
import SvgX from '@jotta/ui/SvgX'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
export const SelectionManager = observer<
  PhotoActionHandlersProps & { restoreOnUnmount?: boolean }
>(function SelectionManager({
  actionContext,
  restoreOnUnmount = false,
  ...props
}) {
  const { addPhotoMode = '', albumId = '' } = props
  const store = useSelectionStore(
    actionContext + addPhotoMode + albumId,
    !restoreOnUnmount,
  )
  const navigate = useNavigate()
  const show = store.hasSelection || Boolean(addPhotoMode)
  const hidden = actionContext === 'TIMELINE_HIDDEN_PHOTOS_SELECTED_PHOTOS'
  const onClose = useCallback(
    (open: boolean) => {
      if (!open) {
        store.selection.clear()
        if (addPhotoMode) {
          navigate(`/photo/album/${addPhotoMode}`)
        }
      }
    },
    [addPhotoMode, store],
  )

  const isMobile = useMobileBreakpoint()

  const mediaStore = useMediaObjectStore()

  const isOwnerOfAllPhotos = useMemo(
    () =>
      !store.values.length ||
      mediaStore
        .getPhotosByIds(store.values)
        .every(photo => photo.username === props.username),
    [store.values, mediaStore, props.username],
  )

  if (!show) {
    return null
  }

  return (
    <>
      {isMobile ? (
        // If mobile, display actions in bottom status bar
        <AppLayoutPortal slot="bottomStatusBar">
          <Dialog.Root open={show} modal={false}>
            <Dialog.Content
              asChild
              onEscapeKeyDown={e => {
                onClose(false)
                e.preventDefault()
              }}
            >
              <BaseSelectionManager
                hidden={hidden}
                selectionCount={store.selection.size}
                onClose={onClose}
                data-testid="SelectionManager"
                data-actioncontext={actionContext}
              >
                <PhotoActionHandlers
                  actionContext={actionContext}
                  photoIds={store.values}
                  isOwnerOfAllPhotos={isOwnerOfAllPhotos}
                  onActionDialogClose={() => {
                    onClose(false)
                  }}
                  onDispatchComplete={(actionType, ...args) => {
                    if (
                      actionType !== 'SHARE_PHOTO' &&
                      actionType !== 'UNSHARE_ALBUM' &&
                      actionType !== 'DOWNLOAD_PHOTO' &&
                      actionType !== 'DOWNLOAD_PHOTOS'
                    ) {
                      onClose(false)
                    }
                  }}
                  {...props}
                />
              </BaseSelectionManager>
            </Dialog.Content>
          </Dialog.Root>
        </AppLayoutPortal>
      ) : (
        // If not mobile, display actions in module nav overlay
        <AppLayoutPortal slot="moduleNavOverlay">
          <div className="relative w-full">
            <div className="flex h-full items-center justify-between rounded-md bg-informative py-1">
              <span className="ml-4 font-medium">
                <Plural
                  value={store.selection.size}
                  one="# photo selected"
                  other="# photos selected"
                />
              </span>
              <div className="flex items-center gap-2">
                {getPhotoActions(actionContext).map(action => (
                  <PhotoActionHandler
                    key={action.actionType}
                    hideDisabled={false}
                    action={action}
                    buttonClass={
                      'btn btn-ghost text-current max-md:[&>span]:hidden'
                    }
                    photoIds={store.values}
                    isOwnerOfAllPhotos={isOwnerOfAllPhotos}
                    onActionDialogClose={() => {
                      onClose(false)
                    }}
                    onDispatchComplete={(actionType, ...args) => {
                      if (
                        actionType !== 'SHARE_PHOTO' &&
                        actionType !== 'UNSHARE_ALBUM' &&
                        actionType !== 'DOWNLOAD_PHOTO' &&
                        actionType !== 'DOWNLOAD_PHOTOS'
                      ) {
                        onClose(false)
                      }
                    }}
                    {...props}
                  />
                ))}
                <button
                  className="btn btn-ghost mx-4 size-5"
                  aria-label={t`Close`}
                  data-testid="SelectionManagerClose"
                  onClick={() => onClose(false)}
                >
                  <PlainBrandIcon icon={SvgX} />
                </button>
              </div>
            </div>
          </div>
        </AppLayoutPortal>
      )}
    </>
  )
})
